import { useEffect, useState } from "react";
import type { FeatureFlagKey } from "@aam/shared";
import { getSessionToken } from "../../hooks/useSession.js";
import { Button } from "../ui/Button.js";
import { PageHeader } from "../ui/PageHeader.js";
import { LoadingStatus, Skeleton } from "../ui/index.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface AdminFeatureFlag {
  key: FeatureFlagKey;
  label: string;
  description: string;
  enabled: boolean;
  updatedAt: string | null;
}

function FeatureFlagsSkeleton() {
  return (
    <section role="status" aria-busy="true" className="space-y-4">
      <LoadingStatus label="Loading feature flags" />
      <Skeleton className="h-28 w-full rounded-card" />
      <Skeleton className="h-28 w-full rounded-card" />
    </section>
  );
}

export function FeatureFlags() {
  const token = getSessionToken();
  const [flags, setFlags] = useState<AdminFeatureFlag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<FeatureFlagKey | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setError(null);
    void fetch(`${API_BASE}/api/admin/feature-flags`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.error || "Unable to load feature flags");
        }
        return json.data as AdminFeatureFlag[];
      })
      .then((data) => {
        if (!cancelled) setFlags(data);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Unable to load feature flags");
        setFlags([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, reloadToken]);

  async function toggleFlag(flag: AdminFeatureFlag) {
    if (!token || savingKey) return;
    setSavingKey(flag.key);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/feature-flags/${flag.key}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ enabled: !flag.enabled }),
      });
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw new Error(json.error || "Unable to update feature flag");
      }
      setFlags((current) =>
        (current ?? []).map((entry) => (entry.key === flag.key ? (json.data as AdminFeatureFlag) : entry))
      );
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to update feature flag");
    } finally {
      setSavingKey(null);
    }
  }

  if (flags === null) {
    return (
      <section className="space-y-6">
        <PageHeader
          title="Feature flags"
          description="Turn product surfaces on or off for agents without a redeploy."
        />
        <FeatureFlagsSkeleton />
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <PageHeader
        title="Feature flags"
        description="Turn product surfaces on or off for agents without a redeploy."
        actions={
          <Button variant="secondary" size="sm" onClick={() => setReloadToken((value) => value + 1)}>
            Refresh
          </Button>
        }
      />

      {error && (
        <p role="alert" className="rounded-card border border-status-rejected-border bg-status-rejected-bg px-4 py-3 text-sm text-status-rejected-text">
          {error}
        </p>
      )}

      <ul className="space-y-3">
        {flags.map((flag) => {
          const saving = savingKey === flag.key;
          return (
            <li
              key={flag.key}
              className="flex flex-col gap-4 rounded-card border-2 border-border-default bg-surface-raised px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-display text-xl font-extrabold text-content-primary">{flag.label}</h2>
                  <span
                    className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-semibold ${
                      flag.enabled
                        ? "border-status-active-border bg-status-active-bg text-status-active-text"
                        : "border-border-default bg-surface-muted text-content-secondary"
                    }`}
                  >
                    {flag.enabled ? "On" : "Off"}
                  </span>
                </div>
                <p className="max-w-2xl text-sm text-content-secondary">{flag.description}</p>
                {flag.updatedAt && (
                  <p className="text-xs text-content-muted">
                    Updated {new Date(flag.updatedAt).toLocaleString()}
                  </p>
                )}
              </div>
              <Button
                variant={flag.enabled ? "secondary" : "primary"}
                disabled={saving}
                aria-pressed={flag.enabled}
                onClick={() => void toggleFlag(flag)}
              >
                {saving ? "Saving…" : flag.enabled ? "Turn off" : "Turn on"}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
